import { ActionBrick, registerBrick, File as OFile, ErrorFlow } from 'olympe';
import { getLogger, httpRequest } from '@olympeio/core';

export default class AzureUploadLargeFileChunks extends ActionBrick {
    /**
     * @override
     * @param {!BrickContext} $
     * @param {string} urlUploadSession
     * @param {OFile} file
     * @param {number} chunkSize
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setErrorFlow
     * @param {function(string)} setItemId
     */
    update($, [urlUploadSession, file, chunkSize], [forwardEvent, setErrorFlow, setItemId]) {
        if (!(file instanceof OFile)) {
            setErrorFlow(ErrorFlow.create('Specified file is not an instance of File', 1));
            return;
        }

        file.getContentAsBinary(
            (arrayBuffer) => {
                this.uploadBinary(urlUploadSession, arrayBuffer, chunkSize)
                    .then((itemId) => {
                        setItemId(itemId);
                        forwardEvent();
                    })
                    .catch((e) => {
                        this.cancelUploadSession(urlUploadSession)
                            .then(() => {
                                setErrorFlow(
                                    ErrorFlow.create(
                                        `An error occurred while uploading file chunk. Upload session has been cancelled. ${e}`,
                                        500
                                    )
                                );
                            })
                            .catch((cancelError) => {
                                setErrorFlow(
                                    ErrorFlow.create(
                                        `An error occurred while cancelling the upload session after having encountered an issue while uploading a file chunk. ${cancelError}`,
                                        500
                                    )
                                );
                            });
                    });
            },
            (err) => {
                setErrorFlow(ErrorFlow.create(`Error when trying to get binary from File: ${err}`, 2));
            }
        );
    }

    /**
     * @private
     * @param {string} url
     * @param {!ArrayBuffer} buffer
     * @param {number} chunkSizeCandidate
     * @return {Promise<string>}
     */
    async uploadBinary(url, buffer, chunkSizeCandidate) {
        const logger = getLogger('AzureUploadLargeFileChunks');
        const byteLength = buffer.byteLength;
        const chunkSize = chunkSizeCandidate > 0 && chunkSizeCandidate < byteLength ? chunkSizeCandidate : byteLength;
        let numberOfCalls = ~~(byteLength / chunkSize);
        if (byteLength % chunkSize > 0) {
            numberOfCalls += 1; // 1 more call is required to upload the rest of the file.
        }

        let response;
        for (let i = 0; i < numberOfCalls; i++) {
            const offset = i * chunkSize;
            const contentLength = Math.min(chunkSize, byteLength - offset);
            const headers = {
                'Content-Length': contentLength,
                'Content-Range': `bytes ${offset}-${Math.min(offset + chunkSize, byteLength) - 1}/${byteLength}`,
                'Content-Type': 'application/octet-stream',
            };

            logger.debug('Upload large file chunks:', JSON.stringify(headers), 'URL:', url);
            response = await httpRequest('PUT', url, headers, new Uint8Array(buffer, offset, contentLength));
        }

        logger.debug('Response after upload:', JSON.stringify(response));
        return response?.id ?? 'no id';
    }

    /**
     * @private
     * @param {string} url
     * @return {Promise<void>}
     */
    async cancelUploadSession(url) {
        getLogger('AzureUploadLargeFileChunks').debug('Cancel upload session with URL:', url);
        await httpRequest('DELETE', url, {});
    }
}

registerBrick('0196394484a395b045ed', AzureUploadLargeFileChunks);
